import React from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag, Minus, Plus, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { money } from "@/lib/shopify";
import { Drawer } from "./Drawer";

export const CartDrawer = ({ open, onClose, cart }) => {
  const { lines, count, loading, busy, update } = cart;
  const c = cart.cart;
  return (
    <Drawer open={open} onClose={onClose} side="right" label="Shopping cart" testId="cart-drawer">
      <div className="flex items-center justify-between px-5 h-16 border-b border-[#8D6E63]/15">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[#2E7D32]" />
          <h2 className="font-display text-xl font-bold text-[#143C18]">Your Cart</h2>
          {count > 0 && <span className="text-xs font-bold bg-[#2E7D32] text-white rounded-full px-2 py-0.5" data-testid="cart-drawer-count">{count}</span>}
        </div>
        <button onClick={onClose} aria-label="Close cart" data-testid="cart-drawer-close" className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-[#F6EBCB] active:scale-95 transition"><X className="w-5 h-5 text-[#143C18]" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4" data-lenis-prevent>
        {loading && !c ? (
          <div className="flex items-center gap-2 text-[#8D6E63] text-sm py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading cart…</div>
        ) : lines.length === 0 ? (
          <div className="text-center py-12" data-testid="cart-empty">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F6EBCB] flex items-center justify-center mb-4"><ShoppingBag className="w-7 h-7 text-[#8D6E63]" /></div>
            <p className="font-display text-xl font-bold text-[#143C18]">Your cart is empty</p>
            <p className="text-sm text-[#5D4037] mt-1">Add something pure and natural.</p>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/a2cow-ghee" onClick={onClose} className="btn-primary justify-center !py-3">Shop A2 Cow Ghee</Link>
              <Link to="/forest-honey" onClick={onClose} className="btn-outline justify-center !py-3">Shop Forest Honey</Link>
            </div>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="cart-lines">
            {lines.map((l) => (
              <li key={l.id} className="flex gap-3 p-3 rounded-2xl bg-white border border-[#8D6E63]/12" data-testid="cart-line">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#F6EBCB] shrink-0">
                  {l.merchandise.product.featuredImage && <img src={l.merchandise.product.featuredImage.url} alt={l.merchandise.product.title} width="80" height="80" className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#143C18] text-sm leading-snug line-clamp-2">{l.merchandise.product.title}</div>
                  <div className="text-sm text-[#8D6E63] mt-0.5">{money(l.merchandise.price.amount, l.merchandise.price.currencyCode)} each</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="flex items-center bg-[#FBF4E1] border border-[#8D6E63]/20 rounded-full overflow-hidden">
                      <button onClick={() => update(l.id, l.quantity - 1)} disabled={busy} aria-label="Decrease" data-testid="cart-line-dec" className="w-10 h-10 flex items-center justify-center active:scale-95 disabled:opacity-40">{l.quantity === 1 ? <Trash2 className="w-4 h-4 text-[#8D6E63]" /> : <Minus className="w-4 h-4 text-[#143C18]" />}</button>
                      <span className="w-8 text-center font-bold text-[#143C18] text-sm tabular-nums" data-testid="cart-line-qty">{l.quantity}</span>
                      <button onClick={() => update(l.id, l.quantity + 1)} disabled={busy} aria-label="Increase" data-testid="cart-line-inc" className="w-10 h-10 flex items-center justify-center active:scale-95 disabled:opacity-40"><Plus className="w-4 h-4 text-[#143C18]" /></button>
                    </div>
                    <div className="font-display font-bold text-[#143C18]">{money(l.cost.totalAmount.amount, l.cost.totalAmount.currencyCode)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {lines.length > 0 && (
        <div className="px-5 pt-4 pb-5 border-t border-[#8D6E63]/15 bg-[#FBF4E1]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-[#5D4037]">Subtotal</span>
            <span className={`font-display font-bold text-[#143C18] ${c.saving > 0 ? "text-lg" : "text-2xl"}`} data-testid="cart-subtotal">{money(c.cost.subtotalAmount.amount, c.cost.subtotalAmount.currencyCode)}</span>
          </div>
          {c.saving > 0 && (
            <>
              <div className="flex items-center justify-between mb-1 text-sm" data-testid="cart-discount">
                <span className="text-[#1F5D25] font-semibold">Discount{c.codes?.length ? ` (${c.codes.join(", ")})` : ""}</span>
                <span className="text-[#1F5D25] font-semibold">− {money(c.saving, c.cost.subtotalAmount.currencyCode)}</span>
              </div>
              <div className="flex items-center justify-between mb-3 pt-2 border-t border-[#8D6E63]/15">
                <span className="text-sm text-[#5D4037]">Total</span>
                <span className="font-display text-2xl font-bold text-[#143C18]" data-testid="cart-total">{money(Number(c.cost.subtotalAmount.amount) - c.saving, c.cost.subtotalAmount.currencyCode)}</span>
              </div>
            </>
          )}
          <p className="text-xs text-[#8D6E63] mb-3">Shipping and payment options are shown at Shopify checkout.</p>
          <a href={c.checkoutUrl} data-testid="cart-checkout-btn" className="btn-primary w-full justify-center !py-4 text-base">
            <ShieldCheck className="w-5 h-5" /> Checkout Securely
          </a>
          {busy && <div className="mt-2 text-xs text-[#8D6E63] flex items-center gap-1 justify-center"><Loader2 className="w-3 h-3 animate-spin" /> Updating…</div>}
        </div>
      )}
    </Drawer>
  );
};
